import type { ReactNode } from 'react';
import { InlineNotification, Modal } from '@carbon/react';
import { occurrenceKeys } from 'utils/occurrence-keys';

interface UndoReconcileModalProps {
  open: boolean;
  undoing: boolean;
  bullets: string[];
  addAsNewCount: number;
  undoError: string | null;
  onCancel: () => void;
  onConfirm: () => void;
  children?: ReactNode;
}

/**
 * Confirms an Undo Reconcile, bulleting out what reverses. Add-as-New conversions
 * are committed on their own and survive the undo, so the modal discloses them.
 * @param {UndoReconcileModalProps} props - Open state, summary content, error and handlers; children render after the bullets
 * @return {JSX.Element} The modal
 */
export function UndoReconcileModal({
  open,
  undoing,
  bullets,
  addAsNewCount,
  undoError,
  onCancel,
  onConfirm,
  children,
}: UndoReconcileModalProps) {
  // Two same-name groups give identical bullets.
  const bulletKeys = occurrenceKeys(bullets);

  return (
    <Modal
      open={open}
      danger
      modalHeading="Undo reconciliation?"
      primaryButtonText={undoing ? 'Undoing…' : 'Undo Reconcile'}
      secondaryButtonText="Cancel"
      primaryButtonDisabled={undoing}
      onRequestClose={onCancel}
      onRequestSubmit={onConfirm}
    >
      <p>This will reverse the following changes:</p>
      <ul>
        {bullets.map((bullet, i) => (
          <li key={bulletKeys[i]}>{bullet}</li>
        ))}
      </ul>
      {children}
      {addAsNewCount > 0 && (
        <p>
          {addAsNewCount === 1
            ? 'The “Add as New” conversion you made in this session was committed separately and will remain in effect.'
            : `The ${addAsNewCount} “Add as New” conversions you made in this session were committed separately and will remain in effect.`}
        </p>
      )}
      {undoError && (
        <InlineNotification
          kind="error"
          lowContrast
          hideCloseButton
          title="Undo failed —"
          subtitle={undoError}
        />
      )}
    </Modal>
  );
}
